"use client";

import React, { useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Switch, Text } from "@lmn/design-system";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";

const FilterWrapper = styled.div<{ $onWhiteBackground?: boolean }>`
  position: relative;
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: ${({ $onWhiteBackground, theme }) =>
    $onWhiteBackground
      ? theme.kallisto.color.background.static.backgroundLayer01
      : theme.kallisto.color.background.static.backgroundPage};
  padding: 1rem;
  border-radius: 8px;

  & label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
  }

  & label > span {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

interface BookOnOptionFilterProps {
  delayedUpdate?: boolean;
  onWhiteBackground?: boolean;
}

const BookOnOptionFilter: React.FC<BookOnOptionFilterProps> = ({
  delayedUpdate = false,
  onWhiteBackground = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();

  // Derive state directly from URL/tempFilters - no local state needed
  const isBookOnOption = delayedUpdate
    ? tempFilters.get("bookOnOption") === "true"
    : searchParams.get("bookOnOption") === "true";

  const handleBookOnOptionChange = useCallback(
    (checked: boolean) => {
      if (delayedUpdate) {
        updateTempFilter("bookOnOption", checked ? "true" : null);
      } else {
        updateFilter("bookOnOption", checked ? "true" : null);
      }
    },
    [updateFilter, updateTempFilter, delayedUpdate]
  );

  return (
    <FilterWrapper $onWhiteBackground={onWhiteBackground}>
      <Switch
        checked={isBookOnOption}
        onChange={handleBookOnOptionChange}
        ltr={true}
        // @ts-expect-error: change this in design system so that it accept React Components
        label={
          <Label>
            <Text variant="BodySmall">
              Kostenfrei stornierbar innerhalb von wenigen Tagen nach Buchung
            </Text>
          </Label>
        }
        aria-describedby="book-on-option-description"
        data-testid="book-on-option-switch"
      />
    </FilterWrapper>
  );
};

export default BookOnOptionFilter;
